// FEAT-12 (spec §3.1) — every coding language, from ONE table: registry.json,
// the hand-edited twin of backend/grading/languages.py LANGUAGE_REGISTRY
// (backend/tests/test_language_registry.py + registry.test.ts pin the two).
// Branch on `RUN_LOCATION` / `CONTRACT`, never on a language name: a language
// added to the registry then works everywhere that reads it.
//
//   run  "browser" → the WASM engines (Pyodide / QuickJS / sql.js) in the tab
//        "server"  → the private code runner, reached only through our API
//   mode "function" (Python, JavaScript, TypeScript) · "sql" · "program" (stdin → stdout)
import registry from "./registry.json";

/** The language ids, in registry order — python / javascript / sql FIRST
 *  (the Gate preload order and editorMode.test.ts depend on it). The union is
 *  spelled here for the type checker; registry.test.ts proves it equals the
 *  JSON. */
export type Language =
  | "python"
  | "javascript"
  | "sql"
  | "typescript"
  | "c"
  | "cpp"
  | "go"
  | "php"
  | "ruby"
  | "perl"
  | "java"
  | "rust"
  | "kotlin"
  | "csharp"
  | "dart"
  | "swift"
  | "scala"
  | "julia";
export type RunLocation = "browser" | "server";
export type Contract = "function" | "sql" | "program";
/** A QUESTION's contract: its language's, or "typed" (FEAT-12 phase 2). */
export type QuestionContract = Contract | "typed";

export interface LanguageEntry {
  id: Language;
  name: string;
  /** Display version: "1.24", "C17 · GCC 14" — see `languageLabel`. */
  version: string;
  run: RunLocation;
  mode: Contract;
  /** The Monaco language id. */
  editorMode: string;
  /** Cheat-proof challenges can twist it (function contracts only). */
  twistable: boolean;
  /** The AI may draft / review it (spec §3.7 — flips a build after its verify). */
  aiAuthoring: boolean;
  /** FEAT-12 phase 2: a question in this server language may carry a
   *  `function_signature` — the TYPED contract (Java, PHP). `mode` stays the
   *  language's default contract; `questionContract` is a question's. */
  functionDriver: boolean;
  /** The AI may draft tests for / review a TYPED question (§3.7 again). */
  typedAi: boolean;
  /** "Still running — compiling {name} can take up to {uiHint}" — how long a
   *  COMPILE takes, so "" exactly when nothing compiles (the browser languages
   *  and the interpreted server ones: PHP, Ruby, Perl, Julia). */
  uiHint: string;
  /** A program language's starter (compiles as-is); "" for the browser ones. */
  starter: string;
}

export const REGISTRY: readonly LanguageEntry[] = registry.languages as LanguageEntry[];

export const SUPPORTED_LANGUAGES: readonly Language[] = REGISTRY.map((e) => e.id);

function byLanguage<T>(pick: (e: LanguageEntry) => T): Record<Language, T> {
  return Object.fromEntries(REGISTRY.map((e) => [e.id, pick(e)])) as Record<Language, T>;
}

/** Where "Run tests" executes. */
export const RUN_LOCATION: Record<Language, RunLocation> = byLanguage((e) => e.run);
/** The test contract. */
export const CONTRACT: Record<Language, Contract> = byLanguage((e) => e.mode);
/** How the language is named to people ("Loading the JavaScript sandbox…"). */
export const SANDBOX_NAME: Record<Language, string> = byLanguage((e) => e.name);
/** The Monaco mode per language. `editorMode.test.ts` checks each mode's
 *  chunk exists in the vendored bundle (public/monaco-v3), so a trim or a
 *  re-vendor can never silently turn a language back into plain text. */
export const EDITOR_MODE: Record<Language, string> = byLanguage((e) => e.editorMode);

export function isLanguage(value: string | null | undefined): value is Language {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value ?? "");
}

export function languageEntry(language: Language): LanguageEntry {
  return REGISTRY.find((e) => e.id === language) ?? REGISTRY[0];
}

/** True for a language whose code runs on our servers (the code runner). */
export function isServerLanguage(language: string | null | undefined): boolean {
  return isLanguage(language) && RUN_LOCATION[language] === "server";
}

/** FEAT-12: a server language THIS server cannot run right now (`available` is
 *  the session's `code_languages`) — every door a pick of it leads to answers
 *  409 LANGUAGE_NOT_AVAILABLE, so a picker shows it but never offers it (the
 *  twin of admin.py's `language_unavailable`). Browser languages never are. */
export function languageUnavailableHere(
  language: string | null | undefined,
  available: readonly string[],
): boolean {
  return isServerLanguage(language) && !available.includes(language as string);
}

/** True for a server language whose questions may be TYPED (a driver exists). */
export function hasFunctionDriver(language: string | null | undefined): boolean {
  return isLanguage(language) && languageEntry(language).functionDriver;
}

/** A question's test contract — the twin of `question_contract` in
 *  languages.py: its language's `mode`, unless it carries a signature AND the
 *  language has a driver. A stored question with no signature keeps exactly
 *  the contract it always had. */
export function questionContract(
  language: Language,
  functionSignature: string | null | undefined,
): QuestionContract {
  if (functionSignature && functionSignature.trim() && hasFunctionDriver(language)) return "typed";
  return CONTRACT[language];
}

/** The AI may draft / review this question: its language's aiAuthoring and,
 *  when typed, typedAi too (§3.7) — the client twin of admin.py's
 *  `_assert_ai_authoring_language` + `_typed_ai_signature`. */
export function aiDrafts(language: Language, typed: boolean): boolean {
  const e = languageEntry(language);
  return e.aiAuthoring && (!typed || e.typedAi);
}

/** FEAT-12 phase 2 (spec §1): a FRESH draft in a driver language starts as
 *  "A function" once that language's `typedAi` flipped — the flag that says
 *  its typed contract's verify closed (Java + PHP, 2026-10-08). Off, the draft
 *  stays a whole program and the contract radio is the only way in. */
export function typedByDefault(language: Language): boolean {
  const e = languageEntry(language);
  return e.functionDriver && e.typedAi;
}

/** "PHP and Java" — the languages with a driver, for copy that names them. */
export function driverLanguageNames(): string {
  const names = REGISTRY.filter((e) => e.functionDriver).map((e) => e.name);
  return names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** JavaScript's test contract — JS argument-list inputs, JS-value expected
 *  outputs, the JSON.stringify cheat-proof twist, the JS function finder's
 *  shape: JavaScript itself and TypeScript (JavaScript once its types are
 *  erased). The twin of `js_contract` in languages.py. */
export function isJsContract(language: string | null | undefined): boolean {
  return language === "javascript" || language === "typescript";
}

/** A one-line note shown to the CANDIDATE under a language's editor, or "".
 *  (Placeholder copy — the wording is Emilia's.) */
export function languageNote(language: Language): string {
  return language === "typescript"
    ? "Types are removed before your code runs and are not checked — a type error never fails a test."
    : "";
}

/** "Go 1.24", "C (C17 · GCC 14)", "SQL (SQLite)" — the backend twin is
 *  `display_name` in languages.py. */
export function languageLabel(language: Language): string {
  const e = languageEntry(language);
  return /^\d/.test(e.version) ? `${e.name} ${e.version}` : `${e.name} (${e.version})`;
}

/** "C, C++, Go, PHP, Ruby, Perl, Java, Rust, Kotlin, C#, Dart, Swift, Scala, Julia" — every language
 *  that runs on our servers, in registry order, for copy that names them. */
export function serverLanguageNames(): string {
  return REGISTRY.filter((e) => e.run === "server")
    .map((e) => e.name)
    .join(", ");
}

/** "Python, JavaScript or SQL" — the languages the AI may draft today (the
 *  registry's aiAuthoring flags), for copy that must stay true as a later
 *  build lifts the ban for another language. `available` is the session's
 *  `codeLanguages`: the server drafts only `available ∩ aiAuthoring`
 *  (`_authoring_languages`), so with the runner dark the copy must not name
 *  the fourteen it cannot run (2026-09-27 pre-ship SIMP-a1). */
export function aiLanguageList(available?: readonly string[]): string {
  const names = REGISTRY.filter(
    (e) => e.aiAuthoring && (available === undefined || available.includes(e.id)),
  ).map((e) => e.name);
  return names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
}
